import React, { useState, useCallback } from 'react'
import { useAnalytics } from '../../../../common/analytics'
import { useIntersectionObserver } from '../../../../common/hooks/useIntersectionObserver'
import { faqData, faqs, SpanishFaqs, SpanishfaqData } from './consts'
import Accordion from './Accordion'
import { Section } from 'src/components/shared/SectionV2'
import Link from 'next/link'
import ArrowIcon from 'src/components/shared/ArrowIcon'

const sectionId = 'debt-relief-faqs'

const Faqs = ({ lang = 'en', variant = 'secondary' }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const { seo, seeMore } = lang === 'es' ? SpanishfaqData : faqData
  const { track } = useAnalytics()

  const isSpanish = lang === 'es'
  const allFaqItems = isSpanish ? SpanishFaqs : faqs
  const canExpand = !isSpanish && allFaqItems.length > 5
  const faqItems = allFaqItems.slice(0, canExpand && !isExpanded ? 5 : 10)

  const { ref: faqsRef } = useIntersectionObserver({
    threshold: 0.5,
    freezeOnceVisible: true,
    onChange: (isIntersecting) => {
      if (isIntersecting) {
        const data = {
          additional_info: { section: sectionId },
          nav_link_section: 'Faqs Page',
          click_type: 'Section View',
          click_id: 'Debt relief FAQs',
          click_url: '/',
          click_text: 'FDR-Web | Debt relief FAQs',
          track_event: 'section_view',
          event_type: 'track',
        }

        track(data, {}, 'section_view')
      }
    },
  })

  const handleLinkClick = useCallback(() => {
    const data = {
      nav_link_section: 'Faqs Page',
      click_type: 'Faq Link Click',
      click_id: 'See all debt relief FAQs',
      click_url: '/faq',
      click_text: 'FDR-Web | See all debt relief FAQs',
      track_event: 'link_click',
      event_type: 'track',
    }

    track(data, {}, 'click')
  }, [track])

  const handleToggleClick = useCallback(() => {
    const action = isExpanded ? 'Show less' : 'Show more'
    const data = {
      nav_link_section: 'Faqs Page',
      click_type: 'Faq Toggle Click',
      click_id: action,
      click_url: '/',
      click_text: `FDR-Web | ${action}`,
      track_event: 'link_click',
      event_type: 'track',
    }

    track(data, {}, 'click')
    setIsExpanded((expanded) => !expanded)
  }, [isExpanded, track])

  return (
    <Section
      variant={variant}
      ref={faqsRef}
      id="faqs"
      data-eh-observe={sectionId}
      data-testid="debt-relief-faqs"
    >
      <div className="max-w-272 mx-auto">
        <Accordion
          items={faqItems}
          title={
            lang === 'es'
              ? 'Preguntas frecuentes sobre el alivio de deudas'
              : 'Debt relief FAQs'
          }
          titleCenter
          questionClassName="text-[18px] lg:text-[24px] font-bold leading-[23.4px] lg:leading-8"
          answerClassName="text-[14px] lg:text-[16px] text-balance font-normal leading-[19.6px] lg:leading-6"
          titleClassName="text-[24px] lg:text-[36px] font-extrabold leading-[31.2px] lg:leading-10"
        />
        {(!canExpand || isExpanded) && (
          <div className="mt-6" onClick={handleLinkClick}>
            <Link
              href="https://www.freedomdebtrelief.com/faq/"
              className="text-content-accent underline"
              data-testid="debt-relief-faqs-see-more"
            >
              {seeMore}
            </Link>
          </div>
        )}
        {canExpand && (
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              className="inline-flex items-center gap-2 px-2 py-1 text-content-accent"
              onClick={handleToggleClick}
              aria-expanded={isExpanded}
            >
              <span className="text-lg font-bold">
                {isExpanded ? 'Show less' : 'Show more'}
              </span>
              <span className="flex items-center" aria-hidden="true">
                <ArrowIcon collapsed={isExpanded} stroke="#041B93" />
              </span>
            </button>
          </div>
        )}
      </div>
    </Section>
  )
}

Faqs.defaultProps = {}

Faqs.propTypes = {}

export default Faqs
